import type { Microsoft365TestResultDto } from '@dbrain/shared';
import { Alert, Button, Icons } from '@dbrain/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useMe } from '../../../api/hooks.js';
import { isOrganizationAdmin } from '../../../pages/admin/sections.js';
import { STATUS_TONE, TEST_FIX_STEP, testCodeOf, type SetupStatus, type SetupStep } from './model.js';

/**
 * Small pieces of Yönetim › Entegrasyonlar › Microsoft 365: the neutral tile (Oppy's own line icon: Microsoft's
 * product icon is never a logo, D-198), the state pill in the Bağlantılar cards' colours, a copy button, the
 * guard of the setup's pages and a test's result in plain words.
 */

export function BuiltinTile({ large = false }: { large?: boolean }) {
  return (
    <span className={`m365a-tile${large ? ' m365a-tile--lg' : ''}`} aria-hidden="true">
      <Icons.plug size={large ? 22 : 20} />
    </span>
  );
}

export function StatusPill({ status, testId = 'm365-admin-status' }: { status: SetupStatus; testId?: string }) {
  const { t } = useTranslation();
  const tone = STATUS_TONE[status];
  return (
    <span className={`connector-state${tone ? ` connector-state--${tone}` : ''}`} data-testid={testId} data-status={status}>
      {t(`microsoft365.admin.status.${status}`)}
    </span>
  );
}

/** "Kopyala": the text to the clipboard, "Kopyalandı" for two seconds. */
export function CopyButton({ text, testId }: { text: string; testId: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);
  return (
    <Button
      size="sm"
      variant="secondary"
      icon={copied ? <Icons.check size={14} /> : <Icons.copy size={14} />}
      onClick={() => {
        void navigator.clipboard
          ?.writeText(text)
          .then(() => setCopied(true))
          .catch(() => setCopied(false));
      }}
      data-testid={testId}
    >
      {copied ? t('microsoft365.admin.copied') : t('microsoft365.admin.copy')}
    </Button>
  );
}

/**
 * The summary and the setup are the firm's organization admins' (a super admin in kurumsal): AdminLayout lets
 * team admins into Entegrasyonlar for their teams' switch, so these pages refuse them here (the API answers 403
 * too). A personal space has no Microsoft 365.
 */
export function FirmAdminOnly({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const me = useMe().data;
  if (!me) return null;
  if (!isOrganizationAdmin(me)) return <Alert tone="warning" title={t('admin.onlyAdmins')} data-testid="admin-only" />;
  if (me.tenant.kind !== 'organization') return <Alert tone="info" title={t('microsoft365.admin.unsupported')} data-testid="m365-unsupported" />;
  return <>{children}</>;
}

/** A test's result in plain words; a failure says what to do and, when it is the setup's to fix, offers the step. */
export function TestOutcome({ result, onFix }: { result: Microsoft365TestResultDto; onFix?: (step: SetupStep) => void }) {
  const { t, i18n } = useTranslation();
  const code = result.ok ? null : testCodeOf(result.code);
  const fix = code ? TEST_FIX_STEP[code] : null;
  const seconds = (result.latencyMs / 1000).toLocaleString(i18n.language, { maximumFractionDigits: 1 });
  return (
    <div className={`m365a-test m365a-test--${result.ok ? 'ok' : 'failed'}`} role="status" data-testid="m365-test-result" data-code={code ?? 'ok'}>
      {result.ok ? <Icons.done size={18} /> : <Icons.error size={18} />}
      <div className="m365a-test__text">
        <strong>{result.ok ? t('microsoft365.admin.setup.test.ok') : t(`microsoft365.admin.testCodes.${code}.title`)}</strong>
        <span>{result.ok ? t('microsoft365.admin.setup.test.answer', { seconds }) : t(`microsoft365.admin.testCodes.${code}.action`)}</span>
        {fix && onFix ? (
          <button type="button" className="link-button" onClick={() => onFix(fix)} data-testid="m365-test-fix">
            {t(fix === 1 ? 'microsoft365.admin.setup.test.openGuide' : 'microsoft365.admin.setup.test.fix')}
          </button>
        ) : null}
      </div>
    </div>
  );
}
